import { supabase } from "@/lib/supabase";
import type { Json, Tables } from "@/types/database";
import type { KeywordVolumeRow } from "@/lib/keyword-volume-parser";

const KEYWORD_VOLUME_LIMIT_OPTIONS = [15, 30, 50, 100] as const;

export type KeywordVolumeLimit = (typeof KEYWORD_VOLUME_LIMIT_OPTIONS)[number];
export type KeywordVolumeLatestRow = Tables<"keyword_volume_latest">;
export type KeywordVolumeSource = Tables<"keyword_volume_sources">;
export type BrandKeywordMismatch = Tables<"brand_keyword_mismatches">;

export interface KeywordVolumeLatestResponse {
  rows: KeywordVolumeLatestRow[];
  totalCount: number;
  page: number;
  limit: KeywordVolumeLimit;
}

/** Volume by month for one Clicks row value; months keyed "YYYY-MM-01". */
export interface KeywordVolumeSeries {
  anchor: string;
  keywords: number;
  byMonth: Record<string, number>;
}

// Values per get_keyword_volume_map call (see getForRows).
const MAP_BATCH = 2500;

// Rows per import call. 500 x 12 months stays well inside the 8 s API timeout.
const BATCH_SIZE = 500;

const toPayload = (rows: KeywordVolumeRow[]) =>
  rows.map((r) => ({ keyword: r.keyword, geo: r.geo, m: r.m })) as unknown as Json;

class KeywordVolumeService {
  private static instance: KeywordVolumeService;

  private constructor() {}

  public static getInstance() {
    if (!KeywordVolumeService.instance) {
      KeywordVolumeService.instance = new KeywordVolumeService();
    }
    return KeywordVolumeService.instance;
  }

  public getAllowedLimits() {
    return KEYWORD_VOLUME_LIMIT_OPTIONS;
  }

  public normalizeLimit(limit: number): KeywordVolumeLimit {
    return (KEYWORD_VOLUME_LIMIT_OPTIONS.includes(limit as KeywordVolumeLimit)
      ? limit
      : 15) as KeywordVolumeLimit;
  }

  /**
   * M1 judged against months already stored, or null when the file does not
   * overlap them enough to say. Confident = at least 20 values compared and
   * 60% of them identical (Google revises a few old values between exports).
   */
  public async matchStoredM1(
    rows: KeywordVolumeRow[],
    fallback: string,
  ): Promise<{ m1Month: string; matched: number; compared: number } | null> {
    const { data, error } = await supabase.rpc("suggest_keyword_m1_month", {
      p_rows: toPayload(rows.slice(0, 300)),
      p_default: fallback,
    });
    if (error) throw new Error(error.message);
    const best = data?.[0];
    if (!best || best.compared < 20 || best.matched / best.compared < 0.6) return null;
    return { m1Month: best.m1_month, matched: best.matched, compared: best.compared };
  }

  public async upload(
    source: "file",
    name: string,
    m1Month: string,
    rows: KeywordVolumeRow[],
    onProgress?: (processed: number, total: number) => void,
  ): Promise<number> {
    const { data: uploadId, error } = await supabase.rpc("keyword_volume_begin_upload", {
      p_source: source,
      p_name: name,
      p_m1_month: m1Month,
    });
    if (error || uploadId == null) throw new Error(error?.message ?? "Cannot start the upload");

    for (let i = 0; i < rows.length; i += BATCH_SIZE) {
      const { error: batchError } = await supabase.rpc("keyword_volume_import_batch", {
        p_upload_id: uploadId,
        p_rows: toPayload(rows.slice(i, i + BATCH_SIZE)),
      });
      if (batchError) throw new Error(batchError.message);
      onProgress?.(Math.min(i + BATCH_SIZE, rows.length), rows.length);
    }

    return uploadId;
  }

  /**
   * Rebuild the page's summary table. Once after a batch of files, not per
   * file: it reads the whole month history.
   */
  public async finishUpload(uploadId: number) {
    const { error } = await supabase.rpc("keyword_volume_finish_upload", { p_upload_id: uploadId });
    if (error) throw new Error(error.message);
  }

  public async getLatest(page: number, limit: number, search = ""): Promise<KeywordVolumeLatestResponse> {
    const normalizedLimit = this.normalizeLimit(limit);
    const from = (page - 1) * normalizedLimit;

    let query = supabase
      .from("keyword_volume_latest")
      .select("*", { count: "exact" })
      .order("volume_avg", { ascending: false, nullsFirst: false })
      .order("keyword_norm", { ascending: true })
      .order("geo", { ascending: true })
      .range(from, from + normalizedLimit - 1);
    if (search.trim()) query = query.ilike("keyword_norm", `%${search.trim().toLowerCase()}%`);

    const { data, error, count } = await query;
    if (error) throw new Error(error.message);
    return { rows: data ?? [], totalCount: count ?? 0, page, limit: normalizedLimit };
  }

  /** Clicks rows: value -> series. Values without a brand keyword are absent. */
  public async getForRows(
    dim: string,
    values: string[],
    countries: string[] = [],
  ): Promise<Map<string, KeywordVolumeSeries>> {
    const out = new Map<string, KeywordVolumeSeries>();
    if (!values.length) return out;

    // One jsonb for all rows: a set-returning RPC is capped at 1000 rows by
    // PostgREST, and every row of the table x 12 months is far more.
    // bind: a method taken off the client loses `this` and throws before any request.
    const rpc = supabase.rpc.bind(supabase) as unknown as (
      fn: string,
      args: Record<string, unknown>,
    ) => Promise<{ data: unknown; error: { message: string; code?: string } | null }>;
    const args = { p_dim: dim, p_values: values, p_countries: countries };
    // Batches in parallel: all-time URL grouping is 11.5k values, and 5k
    // domains alone take 2.8 s against the API's 8 s statement timeout.
    const batches: string[][] = [];
    for (let i = 0; i < values.length; i += MAP_BATCH) batches.push(values.slice(i, i + MAP_BATCH));
    const maps = await Promise.all(
      batches.map((b) => rpc("get_keyword_volume_map", { ...args, p_values: b })),
    );
    if (maps.every((m) => !m.error)) {
      type Entry = { anchor: string; keywords: number; months: Record<string, number | string> };
      for (const map of maps)
        for (const [value, e] of Object.entries((map.data ?? {}) as Record<string, Entry>)) {
          const byMonth: Record<string, number> = {};
          for (const [m, v] of Object.entries(e.months ?? {})) byMonth[m] = Number(v);
          out.set(value, { anchor: e.anchor, keywords: e.keywords, byMonth });
        }
      return out;
    }
    const failed = maps.find((m) => m.error)?.error;
    // A real failure (timeout) must surface; only a missing function falls back.
    if (failed && failed.code !== "PGRST202") throw new Error(failed.message);

    // ponytail: fallback until migration 20260930110000 is deployed; capped at
    // 1000 rows, so only the first ~80 values get data. Drop after the push.
    const { data, error } = await supabase.rpc("get_keyword_volume_for_rows", args);
    if (error) throw new Error(error.message);
    for (const r of data ?? []) {
      const s = out.get(r.value) ?? { anchor: r.anchor, keywords: r.keywords, byMonth: {} };
      s.byMonth[r.month] = Number(r.volume);
      out.set(r.value, s);
    }
    return out;
  }

  /**
   * Monthly volume behind the Clicks filters in a period, like for like: only
   * keyword x geo pairs present in every returned month (see migration
   * 20260930110000). from = null means no lower bound.
   */
  public async getTrend(
    from: string | null,
    to: string,
    filters: Json,
  ): Promise<{ month: string; volume: number; keywords: number }[]> {
    // Cast: not in the generated DB types until 20260930110000 is deployed.
    const { data, error } = await (supabase.rpc.bind(supabase) as unknown as (
      fn: string,
      args: Record<string, unknown>,
    ) => Promise<{ data: { month: string; volume: number | string; keywords: number }[] | null; error: { message: string } | null }>)(
      "get_keyword_volume_trend",
      { p_from: from, p_to: to, p_filters: filters },
    );
    if (error) throw new Error(error.message);
    return (data ?? []).map((r) => ({ month: r.month, volume: Number(r.volume), keywords: r.keywords }));
  }

  /** Newest uploaded month ("YYYY-MM-01"), or null before the first upload. */
  public async getAnchor(): Promise<string | null> {
    const { data, error } = await supabase.from("keyword_volume_latest").select("anchor").limit(1).maybeSingle();
    if (error) throw new Error(error.message);
    return data?.anchor ?? null;
  }

  /** Domains where the Jetsik brand and the Domain Tracker brand_keyword disagree. */
  public async getBrandMismatches(): Promise<BrandKeywordMismatch[]> {
    const { data, error } = await supabase.rpc("get_brand_keyword_mismatches");
    if (error) throw new Error(error.message);
    return data ?? [];
  }

  public async listSources(): Promise<KeywordVolumeSource[]> {
    const { data, error } = await supabase
      .from("keyword_volume_sources")
      .select("*")
      .eq("active", true)
      .order("created_at");
    if (error) throw new Error(error.message);
    return data ?? [];
  }

  public async saveSource(url: string, sheetName: string, intervalMonths: number) {
    const { error } = await supabase.rpc("keyword_volume_save_source", {
      p_sheet_url: url,
      p_sheet_name: sheetName,
      p_interval_months: intervalMonths,
    });
    if (error) throw new Error(error.message);
  }

  public async removeSource(id: number) {
    const { error } = await supabase.rpc("keyword_volume_remove_source", { p_id: id });
    if (error) throw new Error(error.message);
  }

  /** Read the sheet now instead of waiting for its monthly turn. */
  public async syncSource(id: number) {
    const { data, error } = await supabase.functions.invoke("sync-keyword-volumes", {
      body: { source_id: id },
    });
    if (error) throw new Error(error.message);
    if (data?.error) throw new Error(String(data.error));
    return data as { synced: number };
  }
}

export const keywordVolumeService = KeywordVolumeService.getInstance();
